/**
 * Where a report is worked: a live case's report opens in the case page as
 * the workbench, so the report page sends an admin there. Resolves the
 * report's case and reads which of its reports the caller may open in the
 * workbench (`CaseDetail.workbench` — the API decides, never the SPA).
 */

import { useEffect, useState } from 'react';

import { vnextApiClient } from '@/services/vnext-api';

export type WorkbenchRedirect =
  /** Still finding out. */
  | 'resolving'
  /** The report is worked on its own page. */
  | 'here'
  /** The case page's path, where the report opens as the workbench. */
  | { to: string };

/** The workbench's path for a report, where it has one; `reportId`
 * undefined (not an admin, no report) asks nothing and is `here`. */
export function useWorkbenchRedirect(reportId: string | undefined): WorkbenchRedirect {
  const [status, setStatus] = useState<WorkbenchRedirect>(reportId ? 'resolving' : 'here');

  useEffect(() => {
    let cancelled = false;
    if (!reportId) {
      setStatus('here');
      return undefined;
    }
    setStatus('resolving');
    void (async () => {
      try {
        const family = await vnextApiClient.resolveCase('report', reportId);
        const detail = await vnextApiClient.getCase(family);
        if (cancelled) {
          return;
        }
        setStatus(
          detail.workbench.some((w) => w.reportId === reportId)
            ? { to: `/cases/${encodeURIComponent(family)}?item=rep:${reportId}` }
            : 'here'
        );
      } catch {
        if (!cancelled) {
          setStatus('here');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  return status;
}
